import React, { useState } from 'react';
import CenterModal from '../Modal/CenterModal';
import MimaText from '../MimaText';
import styles from '../../assets/styles/Modal/Modal.module.scss';
import { MimaToastUtil } from '../Toast';
import { MimaButton } from '../Button';
import { appStore } from '../../stores';
import { SettingsController } from '../../controllers';
import constant from '../../utils/constant';
import { removeEmptyKeys } from '../../utils/utils';

const DisputeTerms = ({ closeModal }) => {
	const { generalSettings } = appStore;

	const [loading, setLoading] = useState(false);

	const acceptTermsHandler = async () => {
		const website = {
			...generalSettings.website,
			faqs: generalSettings?.website?.faqs?.map(x => ({
				question: x.question,
				text: x.text,
			})),
		};

		const accepted = { ...generalSettings, disputePolicy: true };
		delete accepted?._id;
		delete accepted?.status;
		delete accepted?.business;
		delete accepted?.createdAt;
		delete accepted?.updatedAt;
		delete accepted?.__v;
		delete accepted?.themeData;
		delete accepted?.theme;
		delete accepted?.idVerified;

		removeEmptyKeys(accepted);

		setLoading(true);
		const { status, errorMessage } =
			await SettingsController.updateGeneralConfig(accepted);
		if (status === constant.Success) {
			setLoading(false);
			MimaToastUtil.success({
				message: constant.Success,
			});

			closeModal();
		} else {
			MimaToastUtil.error({
				message: errorMessage,
			});
			setLoading(false);
		}
	};
	return (
		<CenterModal
			closeModal={() => {
				MimaToastUtil.error({
					message:
						'Kindly go through the dispute information and Accept our terms to continue',
				});
			}}
			title={'Dispute Management & Account Risk Exposure Policy'}>
			<MimaText mb={4}>
				By continuing to use Mimapay Africa ("the Platform"), you acknowledge
				and agree to comply with the following Dispute Management & Account Risk
				Exposure Policy. This policy governs actions related to disputes,
				account restrictions, and fund withholding procedures, in order to
				maintain trust and protect platform integrity.
			</MimaText>
			<MimaText variant="subtitleBold">1. Definition of a Dispute</MimaText>
			<MimaText mb={4}>
				For the purpose of this policy, a{' '}
				<span style={{ fontWeight: 600 }}>dispute</span> is defined as any
				formal complaint, claim, chargeback, or request for refund initiated by
				a customer or financial institution, alleging non-delivery of services,
				fraud, misrepresentation, dissatisfaction, or any misconduct requiring
				investigation by the Platform.
			</MimaText>
			<MimaText variant="subtitleBold">
				2. Dispute Escalation & Account Actions
			</MimaText>
			<MimaText mb={2}>
				If a user receives{' '}
				<span style={{ fontWeight: 600 }}>
					two (2) or more disputes within any one (1) to three (3) month period,
				</span>{' '}
				the Platform reserves the right to take the following actions based on
				the severity and nature of the disputes:
			</MimaText>
			<MimaText variant="bodyBold">Account Restriction</MimaText>
			<MimaText mb={2}>
				<li>
					Temporary account suspension for up to{' '}
					<span style={{ fontWeight: 600 }}>14 (fourteen) calendar days.</span>
				</li>
				<li>
					In severe or repeated cases, permanent termination of the user’s
					account and blacklisting from the Platform.
				</li>
			</MimaText>
			<MimaText variant="bodyBold">Fund Withholding</MimaText>
			<MimaText mb={4}>
				In cases involving significant transaction amounts, suspected fraud,
				misrepresentation, or customer harm, the Platform reserves the right to
				withhold funds for up to{' '}
				<span style={{ fontWeight: 600 }}>90 (ninety) calendar days,</span> or
				until the resolution of the dispute is confirmed. This is to safeguard
				affected parties and ensure compliance with financial regulations.
			</MimaText>
			<MimaText variant="subtitleBold">
				3. Dispute Severity Assessment Factors
			</MimaText>
			<MimaText mb={4}>
				The action taken by the Platform will depend on the following factors:
				<li>
					The <span style={{ fontWeight: 600 }}>nature</span> and{' '}
					<span style={{ fontWeight: 600 }}>monetary value</span> of the
					disputed transaction(s).
				</li>
				<li>
					Whether the dispute involves fraud, undelivered service,
					misrepresentation, or intentional misconduct.
				</li>
				<li>
					The user’s{' '}
					<span style={{ fontWeight: 600 }}>past dispute history</span> and
					overall transaction record.
				</li>
				<li>
					The level of <span style={{ fontWeight: 600 }}>cooperation</span>{' '}
					displayed by the user during the dispute resolution process.
				</li>
			</MimaText>
			<MimaText variant="subtitleBold">4. Dispute Monitoring Metrics</MimaText>
			<MimaText mb={1}>
				The Platform monitors disputes in{' '}
				<span style={{ fontWeight: 600 }}>
					rolling 3-month periods (quarters)
				</span>
				using the following risk exposure scale:
			</MimaText>
			<table className={styles.dispute__metrics}>
				<thead>
					<tr>
						<th>Dispute Count (within 3 months)</th>
						<th>Status</th>
						<th>Action Taken</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td>0 Disputes</td>
						<td className={`${styles.status} ${styles.green}`}>Green</td>
						<td>Healthy – No Action Required</td>
					</tr>
					<tr>
						<td>1 Dispute</td>
						<td className={`${styles.status} ${styles.yellow}`}>Yellow</td>
						<td>Formal Warning Notification</td>
					</tr>
					<tr>
						<td>2 Disputes</td>
						<td className={`${styles.status} ${styles.orange}`}>Orange</td>
						<td>2-Week Temporary Account Suspension</td>
					</tr>
					<tr>
						<td>3 or More Disputes</td>
						<td className={`${styles.status} ${styles.red}`}>Red</td>
						<td>Permanent Ban & Account Blacklisting</td>
					</tr>
				</tbody>
			</table>
			<MimaText variant="subtitleBold" mt={4}>
				5. Important Provisions
			</MimaText>
			<MimaText mb={4}>
				<li>
					Users will be formally notified upon dispute escalation and provided
					an opportunity to respond, present evidence, or clarify their position
					within the resolution timeframe.
				</li>
				<li>
					Multiple dispute offenses, even if individually resolved, may result
					in stricter account actions, including permanent suspension.
				</li>
				<li>
					Any withheld funds will be released based on the{' '}
					<span style={{ fontWeight: 600 }}>
						final outcome of the dispute investigation
					</span>{' '}
					and in compliance with applicable laws and financial regulations.
				</li>
				<li>
					The Platform reserves the right to modify this policy as necessary to
					align with legal obligations and maintain platform integrity.
				</li>
			</MimaText>
			<MimaText variant="subtitleBold">
				6. User Acknowledgment & Agreement
			</MimaText>
			<MimaText mb={4}>
				By using the Platform, you hereby acknowledge and consent to the terms
				outlined in this
				<span style={{ fontWeight: 600 }}>
					{' '}
					Dispute Management & Account Risk Exposure Policy.
				</span>{' '}
				You understand and agree that failure to comply with this policy may
				result in account restrictions, withholding of funds, and/or permanent
				account termination, as deemed necessary by the Platform.
			</MimaText>

			<MimaButton
				title="Accept and Continue"
				mt={3}
				onClick={acceptTermsHandler}
				loading={loading}
			/>
		</CenterModal>
	);
};

export default DisputeTerms;
